import React, { useState, useEffect } from 'react';
import { getLandingPageAnalytics } from '../../utils/landingPageAnalytics';
import Pagination from '../../components/Pagination/Pagination';
import './LandingPageAnalytics.scss';

const LandingPageAnalytics = () => {
  const [totalVisits, setTotalVisits] = useState(0);
  const [uniqueVisitors, setUniqueVisitors] = useState(0);
  const [visitorsByIP, setVisitorsByIP] = useState([]);
  const [allVisits, setAllVisits] = useState([]);
  const [totalRegisterClicks, setTotalRegisterClicks] = useState(0);
  const [registerClicksByIP, setRegisterClicksByIP] = useState([]);
  const [allRegisterClicks, setAllRegisterClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination states for IP Visitors
  const [ipPage, setIpPage] = useState(1);
  const [ipPerPage] = useState(20);
  
  // Pagination states for Recent Visits
  const [visitsPage, setVisitsPage] = useState(1);
  const [visitsPerPage] = useState(20);
  
  // Pagination states for Register Clicks
  const [clicksPage, setClicksPage] = useState(1);
  const [clicksPerPage] = useState(20);

  useEffect(() => {
    loadData();
    // Refresh data every 5 seconds
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const analyticsData = await getLandingPageAnalytics();
      
      setTotalVisits(analyticsData.totalVisits || 0);
      setUniqueVisitors(analyticsData.uniqueVisitors || 0);
      setVisitorsByIP(analyticsData.visitorsByIP || []);
      setAllVisits(analyticsData.allVisits || []);
      setTotalRegisterClicks(analyticsData.totalRegisterClicks || 0);
      setRegisterClicksByIP(analyticsData.registerClicksByIP || []);
      setAllRegisterClicks(analyticsData.allRegisterClicks || []);
      setLoading(false);
    } catch (err) {
      console.error('Error loading landing page analytics data:', err);
      setError('Failed to load landing page analytics data. Please check if the server is running.');
      setLoading(false);
    }
  };

  // Calculate pagination for IP visitors
  const ipStartIndex = (ipPage - 1) * ipPerPage;
  const ipEndIndex = ipStartIndex + ipPerPage;
  const paginatedIPs = visitorsByIP.slice(ipStartIndex, ipEndIndex);
  const ipTotalPages = Math.ceil(visitorsByIP.length / ipPerPage);

  // Calculate pagination for recent visits
  const visitsStartIndex = (visitsPage - 1) * visitsPerPage;
  const visitsEndIndex = visitsStartIndex + visitsPerPage;
  const paginatedVisits = allVisits.slice(visitsStartIndex, visitsEndIndex);
  const visitsTotalPages = Math.ceil(allVisits.length / visitsPerPage);

  // Calculate pagination for register clicks
  const clicksStartIndex = (clicksPage - 1) * clicksPerPage;
  const clicksEndIndex = clicksStartIndex + clicksPerPage;
  const paginatedClicks = allRegisterClicks.slice(clicksStartIndex, clicksEndIndex);
  const clicksTotalPages = Math.ceil(allRegisterClicks.length / clicksPerPage);

  // Format date/time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return timestamp;
    }
  };

  if (loading) {
    return <div className="landing-page-analytics-loading">Loading landing page analytics data...</div>;
  }

  if (error) {
    return (
      <div className="landing-page-analytics">
        <div className="analytics-error" style={{ padding: '20px', textAlign: 'center', color: '#ff4444' }}>
          <h3>Error Loading Analytics</h3>
          <p>{error}</p>
          <button onClick={loadData} style={{ marginTop: '10px', padding: '10px 20px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page-analytics">
      <div className="analytics-header">
        <h2>
          <i className="ti-world"></i> Landing Page Analytics
        </h2>
        <p className="subtitle">2026.cashflosummit.in - Visitor Data & Register Clicks</p>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary">
        <div className="summary-card">
          <div className="summary-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <i className="ti-eye"></i>
          </div>
          <div className="summary-content">
            <h3>{totalVisits}</h3>
            <p>Total Visits</p>
          </div>
        </div>
        
        <div className="summary-card">
          <div className="summary-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <i className="ti-user"></i>
          </div>
          <div className="summary-content">
            <h3>{uniqueVisitors}</h3>
            <p>Unique Visitors</p>
          </div>
        </div>
        
        <div className="summary-card">
          <div className="summary-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <i className="ti-mouse"></i>
          </div>
          <div className="summary-content">
            <h3>{totalRegisterClicks}</h3>
            <p>Register Clicks</p>
          </div>
        </div>
      </div>

      {/* Visitors by IP */}
      <div className="analytics-section">
        <h3>
          <i className="ti-bar-chart"></i> Visitors by IP Address
        </h3>
        {paginatedIPs.length > 0 ? (
          <>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>IP Address</th>
                    <th>Total Visits</th>
                    <th>Last Visit</th>
                    <th>Register Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedIPs.map((visitor, index) => {
                    const registerClicks = registerClicksByIP.find(rc => rc.ip === visitor.ip);
                    const clicksCount = registerClicks ? registerClicks.totalClicks : 0;
                    
                    return (
                      <tr key={index}>
                        <td className="ip-cell">{visitor.ip}</td>
                        <td>{visitor.totalVisits}</td>
                        <td>{formatDateTime(visitor.lastVisit)}</td>
                        <td>
                          <span className={clicksCount > 0 ? 'click-badge' : ''}>
                            {clicksCount}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {ipTotalPages > 1 && (
              <div className="pagination-wrapper">
                <Pagination
                  currentPage={ipPage}
                  totalPages={ipTotalPages}
                  onPageChange={setIpPage}
                  itemsPerPage={ipPerPage}
                  totalItems={visitorsByIP.length}
                />
              </div>
            )}
          </>
        ) : (
          <p className="no-data">No visitor data available.</p>
        )}
      </div>

      {/* Recent Visits */}
      <div className="analytics-section">
        <h3>
          <i className="ti-time"></i> Recent Visits
        </h3>
        {paginatedVisits.length > 0 ? (
          <>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>IP Address</th>
                    <th>Route</th>
                    <th>Domain</th>
                    <th>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedVisits.map((visit, index) => (
                    <tr key={index}>
                      <td className="ip-cell">{visit.ip || 'Unknown'}</td>
                      <td>{visit.route || 'N/A'}</td>
                      <td>{visit.domain || '2026.cashflosummit.in'}</td>
                      <td>{formatDateTime(visit.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visitsTotalPages > 1 && (
              <div className="pagination-wrapper">
                <Pagination
                  currentPage={visitsPage}
                  totalPages={visitsTotalPages}
                  onPageChange={setVisitsPage}
                  itemsPerPage={visitsPerPage}
                  totalItems={allVisits.length}
                />
              </div>
            )}
          </>
        ) : (
          <p className="no-data">No visit data available.</p>
        )}
      </div>

      {/* Register Clicks */}
      <div className="analytics-section">
        <h3>
          <i className="ti-mouse"></i> Register Clicks
        </h3>
        {paginatedClicks.length > 0 ? (
          <>
            <div className="table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th>IP Address</th>
                    <th>Domain</th>
                    <th>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClicks.map((click, index) => (
                    <tr key={index}>
                      <td>{click.page || 'Unknown'}</td>
                      <td className="ip-cell">{click.ip || 'Unknown'}</td>
                      <td>{click.domain || '2026.cashflosummit.in'}</td>
                      <td>{formatDateTime(click.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {clicksTotalPages > 1 && (
              <div className="pagination-wrapper">
                <Pagination
                  currentPage={clicksPage}
                  totalPages={clicksTotalPages}
                  onPageChange={setClicksPage}
                  itemsPerPage={clicksPerPage}
                  totalItems={allRegisterClicks.length}
                />
              </div>
            )}
          </>
        ) : (
          <p className="no-data">No register click data available.</p>
        )}
      </div>
    </div>
  );
};

export default LandingPageAnalytics;

